import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoginButton } from '@telegram-auth/react';
import FormTabs from './FormTabs';
import FormSecurityNotice from './FormSecurityNotice';
import ContactForm from './ContactForm';
import TablePagination from './TablePagination';
import { getAuthConfig } from '../Utils/authConfig';
import { 
    SearchIcon, 
    CrossIcon, 
    ClockIcon, 
    InfoIcon, 
    UserCircleIcon, 
    BuyIcon, 
    RentIcon, 
    ForwardIcon, 
    ChevronRightIcon,
    WandIcon
} from './Icons';

// Function to get status label based on status code
export const getStatusLabel = (status) => {
    switch (Number(status)) {
        case 2:
            return 'Under Moderation';
        case 1:
            return 'Parked';
        case 0:
            return 'Offline';
        case -1:
            return 'Inactive';
        default:
            return 'Unknown';
    }
};

// Function to get status color badge
export const getStatusColor = (status) => {
    switch (Number(status)) {
        case 2:
            return 'bg-purple-500/20 text-purple-400 border border-purple-500/30';
        case 1:
            return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
        case 0:
            return 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
        case -1:
            return 'bg-red-500/20 text-red-400 border border-red-500/30';
        default:
            return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
    }
};

const PersonalCabinet = ({ user, onLogin, onLogout, refreshTrigger }) => {
    const location = useLocation();
    const navigate = useNavigate();

    // Read ?tab= from URL, defaulting to 'dashboard'
    const getTabFromUrl = useCallback(() => {
        const params = new URLSearchParams(location.search);
        const tabParam = params.get('tab');
        if (tabParam && ['dashboard', 'domains', 'activity'].includes(tabParam)) {
            return tabParam;
        }
        return 'dashboard';
    }, [location.search]);

    const [activeTab, setActiveTab] = useState(getTabFromUrl);

    // Sync tab when location.search changes
    useEffect(() => {
        setActiveTab(getTabFromUrl());
    }, [getTabFromUrl]);

    // Handle tab switching and preserve all existing search params (e.g., tgWebAppPlatform), stripping any one-time auth params
    const handleTabChange = useCallback((newTab) => {
        setActiveTab(newTab);
        const params = new URLSearchParams(location.search);
        params.delete('auth');
        params.set('tab', newTab);
        navigate({ search: params.toString() }, { replace: true });
    }, [location.search, navigate]);

    const [userDomains, setUserDomains] = useState([]);
    const [userActivity, setUserActivity] = useState([]);
    const [domainSearch, setDomainSearch] = useState('');
    const [activitySearch, setActivitySearch] = useState('');
    const [domainsPage, setDomainsPage] = useState(1);
    const [activityPage, setActivityPage] = useState(1);

    // Reset pagination when search changes
    useEffect(() => {
        setDomainsPage(1);
    }, [domainSearch]);

    useEffect(() => {
        setActivityPage(1);
    }, [activitySearch]);

    const filteredUserDomains = useMemo(() => {
        if (!domainSearch.trim()) return userDomains;
        const q = domainSearch.toLowerCase().trim();
        const tokens = q.split(/\s+/).filter(Boolean);
        return userDomains.filter(d => {
            const statusLabel = getStatusLabel(d.status).toLowerCase();
            const actionText = [
                d.is_sold > 0 ? 'sold' : '',
                d.rent_until && new Date(d.rent_until) > new Date() ? 'rented rent' : '',
                d.forward_until && new Date(d.forward_until) > new Date() ? 'forwarded forward' : ''
            ].join(' ').toLowerCase();
            const combined = `${d.name || ''} ${d.description || ''} ${statusLabel} ${actionText}`.toLowerCase();
            return tokens.every(token => combined.includes(token));
        });
    }, [userDomains, domainSearch]);

    const paginatedUserDomains = useMemo(() => {
        const start = (domainsPage - 1) * 100;
        return filteredUserDomains.slice(start, start + 100);
    }, [filteredUserDomains, domainsPage]);

    const filteredUserActivity = useMemo(() => {
        if (!activitySearch.trim()) return userActivity;
        const q = activitySearch.toLowerCase().trim();
        return userActivity.filter(a => 
            (a.action && a.action.toLowerCase().includes(q)) ||
            (a.details && a.details.toLowerCase().includes(q))
        );
    }, [userActivity, activitySearch]);

    const paginatedUserActivity = useMemo(() => {
        const start = (activityPage - 1) * 100;
        return filteredUserActivity.slice(start, start + 100);
    }, [filteredUserActivity, activityPage]);

    const [editingDomain, setEditingDomain] = useState(null);
    const [minPrices, setMinPrices] = useState({ min_price_buy: 100, min_price_rent: 50, min_price_forward: 10 });

    // Initial state with all 3 monetization options enabled by default
    const [editForm, setEditForm] = useState({ 
        name: '', 
        description: '', 
        enable_buy: true,
        pricebuy: '', 
        discount_buy: '', 
        enable_rent: true,
        pricerent: '', 
        discount_rent: '', 
        enable_forward: true,
        pricefw: '', 
        discount_fw: '' 
    });

    const [showEditModal, setShowEditModal] = useState(false);
    const [showContactForm, setShowContactForm] = useState(false);
    const [accessDisabled, setAccessDisabled] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    
    // Validation error states
    const [editFormErrors, setEditFormErrors] = useState({});
    const [submissionNotice, setSubmissionNotice] = useState('');
    const [isNoticeDismissing, setIsNoticeDismissing] = useState(false);
    const [noticeKey, setNoticeKey] = useState(0);

    const dismissNotice = useCallback(() => {
        setIsNoticeDismissing(true);
        setTimeout(() => {
            setSubmissionNotice('');
            setIsNoticeDismissing(false);
        }, 300);
    }, []);

    const showNotice = useCallback((msg) => {
        setIsNoticeDismissing(false);
        setSubmissionNotice(msg);
        setNoticeKey(prev => prev + 1);
    }, []);

    // Automatically hide notification banner after 10 seconds
    useEffect(() => {
        if (!submissionNotice) return;
        setIsNoticeDismissing(false);
        const timer = setTimeout(() => {
            dismissNotice();
        }, 10000);

        return () => clearTimeout(timer);
    }, [submissionNotice, noticeKey, dismissNotice]);

    // Get JWT token from localStorage (unified token fallback)
    const getAuthToken = useCallback(() => {
        return localStorage.getItem('token') || localStorage.getItem('adminToken');
    }, []);

    // Set JWT token in localStorage
    const setAuthToken = useCallback((token) => {
        localStorage.setItem('token', token);
    }, []);

    // Terminate user session if disabled by administrator
    const handleDisabledUserSession = useCallback(() => {
        setAccessDisabled(true);
        localStorage.removeItem('token');
        localStorage.removeItem('telegram_user');
        localStorage.removeItem('telegramUser');
        window.dispatchEvent(new Event('domainer_auth_change'));
        window.dispatchEvent(new Event('authChange'));
    }, []);

    // Check if user access is disabled
    const checkUserAccess = useCallback(async () => {
        if (!user) return;
        
        try {
            const token = getAuthToken();
            const response = await fetch(`/api/user-access/${user.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.status === 403) {
                handleDisabledUserSession();
                return;
            }
            if (response.ok) {
                const data = await response.json();
                if (data.disabled === 1) {
                    handleDisabledUserSession();
                } else {
                    setAccessDisabled(false);
                }
            } else {
                console.error('Failed to check user access, status:', response.status);
            }
        } catch (error) {
            console.error('Error checking user access:', error);
        }
    }, [user, getAuthToken, handleDisabledUserSession]);

    const fetchAuthConfig = useCallback(async () => {
        try {
            const data = await getAuthConfig();
            if (data) {
                if (data.authEnabled === false) {
                    const adminToken = localStorage.getItem('adminToken');
                    if (user?.role !== 'admin' && !adminToken) {
                        handleDisabledUserSession();
                        return;
                    }
                }
                const minP = data.min_prices || data.minPrices;
                if (minP) {
                    const buy = minP.buy || minP.min_price_buy || 100;
                    const rent = minP.rent || minP.min_price_rent || 50;
                    const fwd = minP.forward || minP.min_price_forward || 10;
                    setMinPrices({ min_price_buy: buy, min_price_rent: rent, min_price_forward: fwd });
                }
            }
        } catch (e) {
            console.error('Failed to load auth config:', e);
        }
    }, [user, handleDisabledUserSession]);

    const fetchUserDomains = useCallback(async () => {
        if (!user) return;
        
        try {
            const token = getAuthToken();
            const response = await fetch(`/api/user-domains/${user.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.status === 403 || response.status === 401) {
                handleDisabledUserSession();
                return;
            }
            if (response.ok) {
                const domains = await response.json();
                setUserDomains(domains);
            } else {
                console.error('Failed to fetch domains, status:', response.status);
            }
        } catch (error) {
            console.error('Error fetching domains:', error);
        }
    }, [user, getAuthToken, handleDisabledUserSession]);

    const fetchUserActivity = useCallback(async () => {
        if (!user) return;
        
        try {
            const token = getAuthToken();
            const response = await fetch(`/api/user-activity/${user.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            if (response.status === 403 || response.status === 401) {
                handleDisabledUserSession();
                return;
            }
            if (response.ok) {
                const activity = await response.json();
                setUserActivity(activity);
            } else {
                console.error('Failed to fetch activity log, status:', response.status);
            }
        } catch (error) {
            console.error('Error fetching activity log:', error);
        }
    }, [user, getAuthToken, handleDisabledUserSession]);

    // Batch fetch user cabinet data in a single round-trip
    const fetchCabinetData = useCallback(async () => {
        if (!user) return;
        const token = getAuthToken();
        try {
            const response = await fetch(`/api/user-cabinet-data/${user.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.status === 403 || response.status === 401) {
                handleDisabledUserSession();
                return;
            }
            if (response.ok) {
                const data = await response.json();
                if (data.disabled === 1) {
                    handleDisabledUserSession();
                    return;
                }
                setAccessDisabled(false);
                if (Array.isArray(data.domains)) {
                    setUserDomains(data.domains);
                }
                if (Array.isArray(data.activity)) {
                    setUserActivity(data.activity);
                }
                return;
            }
        } catch (e) {
            console.warn('Batch cabinet data fetch failed, using fallback:', e);
        }

        // Fallback to individual requests if batch endpoint is unavailable
        checkUserAccess();
        fetchUserDomains();
        fetchUserActivity();
    }, [user, getAuthToken, handleDisabledUserSession, checkUserAccess, fetchUserDomains, fetchUserActivity]);

    // Load user data and domains on mount or when user/refreshTrigger changes (NOT on tab switches)
    useEffect(() => {
        if (user) {
            fetchAuthConfig();
            fetchCabinetData();
        }
    }, [user, refreshTrigger, fetchAuthConfig, fetchCabinetData]);

    useEffect(() => {
        const handleDomainsUpdated = () => {
            fetchCabinetData();
        };
        window.addEventListener('domainer_domains_updated', handleDomainsUpdated);
        return () => window.removeEventListener('domainer_domains_updated', handleDomainsUpdated);
    }, [fetchCabinetData]);

    const showDisabledUserError = useCallback(() => {
        setShowErrorModal(true);
    }, []);

    // Pricing calculation based on domain name length
    const calculateLengthPrices = useCallback((domainName) => {
        let clean = (domainName || '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0].split(':')[0];
        if (!clean || !clean.includes('.')) {
            clean = 'mybrand.com';
        }
        const sld = clean.split('.')[0] || 'brand';
        const len = Math.max(1, sld.length);

        let buyPrice = 1000;
        if (len === 1) buyPrice = 100000;
        else if (len === 2) buyPrice = 50000;
        else if (len === 3) buyPrice = 25000;
        else if (len === 4) buyPrice = 10000;
        else if (len === 5) buyPrice = 6000;
        else if (len === 6) buyPrice = 4000;
        else if (len === 7) buyPrice = 2500;
        else if (len === 8) buyPrice = 1500;
        else buyPrice = 1000;

        const minBuy = minPrices.min_price_buy || 100;
        const minRent = minPrices.min_price_rent || 50;
        const minFw = minPrices.min_price_forward || 10;

        buyPrice = Math.max(minBuy, buyPrice);
        const rentPrice = Math.max(minRent, Math.round((buyPrice * 0.05) / 10) * 10);
        const fwPrice = Math.max(minFw, Math.round(rentPrice / 30));

        return { clean, buyPrice, rentPrice, fwPrice };
    }, [minPrices]);

    const [isAiFillingEdit, setIsAiFillingEdit] = useState(false);
    const [showEditAiConfirm, setShowEditAiConfirm] = useState(false);

    // Execute Auto-fill for Edit Domain
    const executeAutoFillEdit = useCallback(async () => {
        setShowEditAiConfirm(false);
        const { buyPrice, rentPrice, fwPrice } = calculateLengthPrices(editForm.name);
        setIsAiFillingEdit(true);

        let aiDescription = null;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch('/api/ai/autofill-domain', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({ domain_name: editForm.name })
            });
            const data = await res.json();
            if (data && data.success && data.description) {
                aiDescription = data.description;
            }
        } catch (e) {
            console.warn('AI edit autofill fallback:', e);
        } finally {
            setIsAiFillingEdit(false);
        }

        setEditForm(prev => ({
            ...prev,
            description: aiDescription || 'Premium high-value domain suitable for technology, finance, or modern digital services.',
            enable_buy: true,
            pricebuy: String(buyPrice),
            discount_buy: '',
            enable_rent: true,
            pricerent: String(rentPrice),
            discount_rent: '',
            enable_forward: true,
            pricefw: String(fwPrice),
            discount_fw: ''
        }));
        setEditFormErrors({});
    }, [calculateLengthPrices, editForm.name]);

    const handleAutoFillEditDomain = useCallback(() => {
        if (editForm.description && editForm.description.trim().length > 0) {
            setShowEditAiConfirm(true);
        } else {
            executeAutoFillEdit();
        }
    }, [editForm.description, executeAutoFillEdit]);

    const validateDescription = (description) => {
        if (!description) return 'Description is required';
        if (description.length > 250) return 'Description must be no more than 250 characters';
        const descriptionRegex = /^[a-zA-Z0-9\s.,;:!?()\-'"@#$%^&*+=[\]{}|\\/<>`~]*$/;
        if (!descriptionRegex.test(description)) return 'Description contains invalid characters';
        return '';
    };

    const handleEditDomain = useCallback((domain) => {
        if (accessDisabled) {
            showDisabledUserError();
            return;
        }
        
        setEditingDomain(domain);
        setEditForm({
            name: domain.name,
            description: domain.pending_description || domain.description || '',
            enable_buy: Boolean(domain.pricebuy && Number(domain.pricebuy) > 0),
            pricebuy: domain.pricebuy || String(minPrices.min_price_buy || 100),
            discount_buy: domain.discount_buy !== null && domain.discount_buy !== undefined ? domain.discount_buy : '',
            enable_rent: Boolean(domain.pricerent && Number(domain.pricerent) > 0),
            pricerent: domain.pricerent || String(minPrices.min_price_rent || 50),
            discount_rent: domain.discount_rent !== null && domain.discount_rent !== undefined ? domain.discount_rent : '',
            enable_forward: Boolean(domain.pricefw && Number(domain.pricefw) > 0),
            pricefw: domain.pricefw || String(minPrices.min_price_forward || 10),
            discount_fw: domain.discount_fw !== null && domain.discount_fw !== undefined ? domain.discount_fw : ''
        });
        setEditFormErrors({});
        setShowEditModal(true);
    }, [accessDisabled, showDisabledUserError, minPrices]);

    const handleUpdateDomain = useCallback(async () => {
        if (accessDisabled) {
            showDisabledUserError();
            return;
        }
        
        if (!editingDomain) return;
        
        // Frontend validation
        const errors = {};
        const descriptionError = validateDescription(editForm.description);
        if (descriptionError) errors.description = descriptionError;

        if (!editForm.enable_buy && !editForm.enable_rent && !editForm.enable_forward) {
            errors.general = 'Please select at least one monetization option: Buy, Rent, or Forwarding';
        }

        if (editForm.enable_buy) {
            const pb = Number(editForm.pricebuy);
            if (!editForm.pricebuy || isNaN(pb) || pb < (minPrices.min_price_buy || 100)) {
                errors.pricebuy = `Minimum buy price: $${minPrices.min_price_buy || 100}`;
            }
            if (editForm.discount_buy !== '' && editForm.discount_buy !== null && editForm.discount_buy !== undefined) {
                const db = Number(editForm.discount_buy);
                if (isNaN(db) || db < 0 || db > 50) {
                    errors.discount_buy = 'Discount cannot exceed 50%';
                }
            }
        }

        if (editForm.enable_rent) {
            const pr = Number(editForm.pricerent);
            if (!editForm.pricerent || isNaN(pr) || pr < (minPrices.min_price_rent || 50)) {
                errors.pricerent = `Minimum rent price: $${minPrices.min_price_rent || 50}`;
            }
            if (editForm.discount_rent !== '' && editForm.discount_rent !== null && editForm.discount_rent !== undefined) {
                const dr = Number(editForm.discount_rent);
                if (isNaN(dr) || dr < 0 || dr > 50) {
                    errors.discount_rent = 'Discount cannot exceed 50%';
                }
            }
        }

        if (editForm.enable_forward) {
            const pf = Number(editForm.pricefw);
            if (!editForm.pricefw || isNaN(pf) || pf < (minPrices.min_price_forward || 10)) {
                errors.pricefw = `Minimum forward price: $${minPrices.min_price_forward || 10}`;
            }
            if (editForm.discount_fw !== '' && editForm.discount_fw !== null && editForm.discount_fw !== undefined) {
                const df = Number(editForm.discount_fw);
                if (isNaN(df) || df < 0 || df > 50) {
                    errors.discount_fw = 'Discount cannot exceed 50%';
                }
            }
        }
        
        if (Object.keys(errors).length > 0) {
            setEditFormErrors(errors);
            return;
        }
        
        try {
            const token = getAuthToken();
            const response = await fetch(`/api/domains/${editingDomain.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    description: editForm.description,
                    enable_buy: editForm.enable_buy,
                    pricebuy: editForm.enable_buy ? Number(editForm.pricebuy) : null,
                    discount_buy: editForm.enable_buy && editForm.discount_buy !== '' ? Number(editForm.discount_buy) : null,
                    enable_rent: editForm.enable_rent,
                    pricerent: editForm.enable_rent ? Number(editForm.pricerent) : null,
                    discount_rent: editForm.enable_rent && editForm.discount_rent !== '' ? Number(editForm.discount_rent) : null,
                    enable_forward: editForm.enable_forward,
                    pricefw: editForm.enable_forward ? Number(editForm.pricefw) : null,
                    discount_fw: editForm.enable_forward && editForm.discount_fw !== '' ? Number(editForm.discount_fw) : null
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                setShowEditModal(false);
                setEditFormErrors({});
                if (data.message) {
                    showNotice(data.message);
                }
                fetchUserDomains();
                fetchUserActivity();
                
                await fetch('/api/update-user-activity', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({})
                });
            } else {
                let errorMsg = 'Failed to update domain';
                try {
                    const error = await response.json();
                    if (response.status === 403 && (error.disabled || error.session_terminated)) {
                        handleDisabledUserSession();
                        setShowEditModal(false);
                        return;
                    }
                    errorMsg = error.error || error.message || errorMsg;
                } catch (_) {
                    try {
                        const text = await response.text();
                        if (text && text.length < 150) errorMsg = text;
                    } catch (__) {}
                }
                setEditFormErrors({ general: errorMsg });
            }
        } catch (error) {
            console.error('Error updating domain:', error);
            setEditFormErrors({ general: 'Network error updating domain' });
        }
    }, [accessDisabled, showDisabledUserError, editingDomain, editForm, minPrices, getAuthToken, showNotice, fetchUserDomains, fetchUserActivity, handleDisabledUserSession]);

    const handleDeleteDomain = useCallback(async (domainId, domainName) => {
        if (accessDisabled) {
            showDisabledUserError();
            return;
        }
        
        if (!window.confirm(`Are you sure you want to delete domain ${domainName}?`)) {
            return;
        }
        
        try {
            const token = getAuthToken();
            const response = await fetch(`/api/domains/${domainId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({})
            });
            
            if (response.ok) {
                showNotice(`Domain '${domainName}' has been successfully deleted.`);
                fetchUserDomains();
                fetchUserActivity();
                
                await fetch('/api/update-user-activity', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({})
                });
            } else {
                const error = await response.json().catch(() => ({}));
                if (response.status === 403 && (error.disabled || error.session_terminated)) {
                    handleDisabledUserSession();
                    return;
                }
                showNotice(`Error deleting domain: ${error.error || error.message || 'Failed to delete domain'}`);
            }
        } catch (error) {
            console.error('Error deleting domain:', error);
            showNotice('Error deleting domain. Please check your connection and try again.');
        }
    }, [accessDisabled, showDisabledUserError, getAuthToken, showNotice, fetchUserDomains, fetchUserActivity, handleDisabledUserSession]);

    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    if (!user) {
        return (
            <div className="py-20 px-6 md:px-8 lg:px-16">
                <div className="max-w-4xl mx-auto">
                    <div className="card p-12 text-center space-y-6">
                        <h2 className="text-3xl font-display font-bold text-white">Personal Cabinet</h2>
                        <p className="text-neutral-400">
                            Please log in with Telegram to access your personal cabinet
                        </p>
                        <div className="flex justify-center">
                            <LoginButton
                                botUsername={process.env.REACT_APP_TELEGRAM_BOT_USERNAME || ''}
                                onAuthCallback={async (data) => {
                                    try {
                                        const response = await fetch('/api/validate-telegram-auth', {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json',
                                            },
                                            body: JSON.stringify(data),
                                        });
                                        
                                        const result = await response.json();
                                        
                                        if (result.success) {
                                            setAuthToken(result.token);
                                            if (result.adminToken || result.user?.role === 'admin') {
                                                localStorage.setItem('adminToken', result.adminToken || result.token);
                                            }
                                            localStorage.setItem('telegram_user', JSON.stringify(result.user));
                                            localStorage.setItem('telegramUser', JSON.stringify(result.user));
                                            window.dispatchEvent(new Event('domainer_auth_change'));
                                            window.dispatchEvent(new Event('authChange'));
                                            onLogin(result.user);
                                        } else {
                                            if (result.disabled) {
                                                handleDisabledUserSession();
                                            }
                                            alert(result.error || result.message || 'Telegram authentication failed');
                                            console.error('Telegram authentication failed:', result.message || result.error);
                                        }
                                    } catch (error) {
                                        console.error('Error validating Telegram authentication:', error);
                                    }
                                }}
                                buttonSize="large"
                                cornerRadius={5}
                                showAvatar={true}
                                lang="en"
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (accessDisabled) {
        return (
            <div className="py-20 px-6 md:px-8 lg:px-16">
                <div className="max-w-4xl mx-auto">
                    <div className="card p-12 text-center space-y-6">
                        <h2 className="text-3xl font-display font-bold text-white">Cabinet Temporarily Disabled</h2>
                        <p className="text-neutral-400">
                            Your personal cabinet has been temporarily disabled. Please contact support for more information.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    const isAdmin = Boolean(user?.role === 'admin' || (typeof window !== 'undefined' && localStorage.getItem('adminToken')));

    return (
        <div className="py-20 px-6 md:px-8 lg:px-16">
            <div className="max-w-6xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-3xl font-display font-bold text-white">Personal Cabinet</h1>
                            {isAdmin && (
                                <span className="px-2.5 py-0.5 rounded-full bg-primary-500/15 border border-primary-500/30 text-primary-300 text-xs font-semibold">
                                    Admin
                                </span>
                            )}
                        </div>
                        <div className="flex items-center mt-2">
                            {user.photo_url ? (
                                <img 
                                    src={user.photo_url} 
                                    alt={user.first_name || 'User'} 
                                    className="w-10 h-10 rounded-full mr-3 object-cover border border-white/10"
                                />
                            ) : (
                                <div className="w-10 h-10 rounded-full mr-3 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 border border-primary-500/30 flex items-center justify-center text-primary-400">
                                    <UserCircleIcon className="w-6 h-6" />
                                </div>
                            )}
                            <div>
                                <p className="text-white font-medium">
                                    {user.first_name} {user.last_name}
                                    {user.username && <span className="text-neutral-400 text-sm ml-2">@{user.username}</span>}
                                </p>
                                <p className="text-neutral-400 text-sm">Telegram ID: {user.telegram_id || user.id}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation Tabs (Unified table, no separate submissions tab) */}
                <div className="flex border-b border-dark-300 mb-8 overflow-x-auto">
                    <button
                        onClick={() => handleTabChange('dashboard')}
                        className={`flex items-center gap-2 px-4 py-2 font-medium text-sm whitespace-nowrap transition-colors ${
                            activeTab === 'dashboard'
                                ? 'text-primary-400 border-b-2 border-primary-400'
                                : 'text-neutral-400 hover:text-white'
                        }`}
                    >
                        <UserCircleIcon className="w-4 h-4 shrink-0" />
                        <span>Dashboard</span>
                    </button>
                    <button
                        onClick={() => handleTabChange('domains')}
                        className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${
                            activeTab === 'domains'
                                ? 'text-primary-400 border-b-2 border-primary-400'
                                : 'text-neutral-400 hover:text-white'
                        }`}
                    >
                        My Domains ({userDomains.length})
                    </button>
                    <button
                        onClick={() => handleTabChange('activity')}
                        className={`px-4 py-2 font-medium text-sm whitespace-nowrap ${
                            activeTab === 'activity'
                                ? 'text-primary-400 border-b-2 border-primary-400'
                                : 'text-neutral-400 hover:text-white'
                        }`}
                    >
                        Activity Log
                    </button>
                </div>

                {submissionNotice && (
                    <div 
                        role="alert"
                        aria-live="polite"
                        className={`relative overflow-hidden mb-6 rounded-2xl border backdrop-blur-xl shadow-xl transition-all duration-300 ease-out ${
                            isNoticeDismissing 
                                ? 'opacity-0 -translate-y-2 scale-98 max-h-0 py-0 mb-0 pointer-events-none' 
                                : 'opacity-100 translate-y-0 scale-100 max-h-40 p-4'
                        } ${
                            String(submissionNotice).toLowerCase().includes('delete') || String(submissionNotice).toLowerCase().includes('error')
                                ? 'bg-rose-950/40 border-rose-500/30 text-rose-200 shadow-rose-950/20'
                                : String(submissionNotice).toLowerCase().includes('moderation') || String(submissionNotice).toLowerCase().includes('review')
                                    ? 'bg-amber-950/40 border-amber-500/30 text-amber-200 shadow-amber-950/20'
                                    : 'bg-emerald-950/40 border-emerald-500/30 text-emerald-200 shadow-emerald-950/20'
                        }`}
                    >
                        <style>{`
                            @keyframes domainerNoticeCountdown {
                                from { width: 100%; }
                                to { width: 0%; }
                            }
                        `}</style>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                                <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center border shadow-inner ${
                                    String(submissionNotice).toLowerCase().includes('delete') || String(submissionNotice).toLowerCase().includes('error')
                                        ? 'bg-rose-500/15 border-rose-500/30 text-rose-400'
                                        : String(submissionNotice).toLowerCase().includes('moderation') || String(submissionNotice).toLowerCase().includes('review')
                                            ? 'bg-amber-500/15 border-amber-500/30 text-amber-400'
                                            : 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                                }`}>
                                    {String(submissionNotice).toLowerCase().includes('delete') ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    ) : String(submissionNotice).toLowerCase().includes('error') ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    ) : String(submissionNotice).toLowerCase().includes('moderation') || String(submissionNotice).toLowerCase().includes('review') ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </div>
                                <div className="space-y-0.5 pt-0.5">
                                    <div className="flex items-center gap-2">
                                        <span className="font-semibold text-xs tracking-wider uppercase opacity-90">
                                            {String(submissionNotice).toLowerCase().includes('delete')
                                                ? 'Domain Removed'
                                                : String(submissionNotice).toLowerCase().includes('error')
                                                    ? 'Attention'
                                                    : String(submissionNotice).toLowerCase().includes('moderation') || String(submissionNotice).toLowerCase().includes('review')
                                                        ? 'Under Moderation'
                                                        : 'Success'}
                                        </span>
                                    </div>
                                    <p className="text-sm font-medium leading-relaxed">{submissionNotice}</p>
                                </div>
                            </div>
                            <button 
                                onClick={dismissNotice} 
                                title="Close notification"
                                className="p-1.5 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        {/* 10-second countdown progress bar */}
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20 overflow-hidden">
                            <div 
                                key={noticeKey}
                                className={`h-full ${
                                    String(submissionNotice).toLowerCase().includes('delete') || String(submissionNotice).toLowerCase().includes('error')
                                        ? 'bg-rose-400'
                                        : String(submissionNotice).toLowerCase().includes('moderation') || String(submissionNotice).toLowerCase().includes('review')
                                            ? 'bg-amber-400'
                                            : 'bg-emerald-400'
                                }`}
                                style={{
                                    animation: 'domainerNoticeCountdown 10s linear forwards'
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Dashboard Tab */}
                {activeTab === 'dashboard' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <div className="card p-6">
                            <h3 className="text-lg font-display font-bold text-white mb-4">Account Summary</h3>
                            <div className="space-y-2">
                                <div 
                                    onClick={() => handleTabChange('domains')}
                                    className="flex justify-between items-center p-2.5 rounded-xl hover:bg-white/[0.04] transition-all cursor-pointer group"
                                    title="View all domains"
                                >
                                    <span className="text-neutral-400 group-hover:text-primary-300 transition-colors flex items-center gap-1.5 text-sm">
                                        <span>Total Domains</span>
                                        <ChevronRightIcon className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-primary-400" />
                                    </span>
                                    <span className="text-white font-medium group-hover:text-primary-300 transition-colors text-sm">{userDomains.length}</span>
                                </div>
                                <div 
                                    onClick={() => handleTabChange('domains')}
                                    className="flex justify-between items-center p-2.5 rounded-xl hover:bg-white/[0.04] transition-all cursor-pointer group"
                                    title="View parked domains"
                                >
                                    <span className="text-neutral-400 group-hover:text-primary-300 transition-colors flex items-center gap-1.5 text-sm">
                                        <span>Parked Domains</span>
                                        <ChevronRightIcon className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-primary-400" />
                                    </span>
                                    <span className="text-white font-medium group-hover:text-primary-300 transition-colors text-sm">{userDomains.filter(domain => domain.status === 1).length}</span>
                                </div>
                                <div 
                                    onClick={() => handleTabChange('domains')}
                                    className="flex justify-between items-center p-2.5 rounded-xl hover:bg-white/[0.04] transition-all cursor-pointer group"
                                    title="View domains under moderation"
                                >
                                    <span className="text-neutral-400 group-hover:text-purple-300 transition-colors flex items-center gap-1.5 text-sm">
                                        <span>Under Moderation</span>
                                        <ChevronRightIcon className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-purple-400" />
                                    </span>
                                    <span className="text-purple-400 font-medium text-sm">{userDomains.filter(domain => domain.status === 2).length}</span>
                                </div>
                                <div className="flex justify-between items-center p-2.5 rounded-xl text-sm">
                                    <span className="text-neutral-400">Account Status</span>
                                    <span className={`font-medium ${accessDisabled ? 'text-red-400' : 'text-green-400'}`}>
                                        {accessDisabled ? 'Disabled' : 'Active'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="card p-6 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-display font-bold text-white">Recent Activity</h3>
                                    <button
                                        type="button"
                                        onClick={() => handleTabChange('activity')}
                                        className="text-xs text-primary-400 hover:text-primary-300 transition-colors inline-flex items-center gap-1 group font-medium cursor-pointer"
                                    >
                                        <span>View all</span>
                                        <ChevronRightIcon className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {userActivity.slice(0, 3).map((activity, index) => (
                                        <div 
                                            key={index} 
                                            onClick={() => handleTabChange('activity')}
                                            className="p-2.5 rounded-xl hover:bg-white/[0.04] transition-all cursor-pointer group"
                                            title="View in activity log"
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <p className="text-white text-sm font-medium group-hover:text-primary-300 transition-colors capitalize">
                                                    {activity.action.replace(/_/g, ' ')}
                                                </p>
                                                <ChevronRightIcon className="w-3 h-3 text-neutral-500 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-primary-400 shrink-0" />
                                            </div>
                                            <p className="text-neutral-400 text-xs mt-0.5">{formatTimestamp(activity.timestamp)}</p>
                                        </div>
                                    ))}
                                    {userActivity.length === 0 && (
                                        <p className="text-neutral-400 text-sm py-4 text-center">No recent activity</p>
                                    )}
                                </div>
                            </div>
                            {userActivity.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => handleTabChange('activity')}
                                    className="w-full mt-3 py-2 px-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-xs font-medium text-neutral-300 hover:text-white transition-all text-center border border-white/[0.06] hover:border-white/10"
                                >
                                    Open Activity Log →
                                </button>
                            )}
                        </div>

                        <div className="card p-6">
                            <h3 className="text-lg font-display font-bold text-white mb-4">Quick Actions</h3>
                            <div className="space-y-2.5">
                                <button 
                                    type="button"
                                    onClick={() => handleTabChange('domains')}
                                    className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-white text-sm border border-white/[0.06] hover:border-white/10 transition-all group cursor-pointer"
                                >
                                    <span>Manage Domains</span>
                                    <ChevronRightIcon className="w-3.5 h-3.5 text-neutral-400 group-hover:text-primary-400 group-hover:translate-x-0.5 transition-all" />
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => handleTabChange('activity')}
                                    className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-white text-sm border border-white/[0.06] hover:border-white/10 transition-all group cursor-pointer"
                                >
                                    <span>View Activity Log</span>
                                    <ChevronRightIcon className="w-3.5 h-3.5 text-neutral-400 group-hover:text-primary-400 group-hover:translate-x-0.5 transition-all" />
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => setShowContactForm(true)}
                                    className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-primary-500/10 hover:bg-primary-500/20 text-primary-300 hover:text-white text-sm border border-primary-500/20 hover:border-primary-500/30 transition-all group cursor-pointer"
                                >
                                    <span>+ Add New Domain</span>
                                    <ChevronRightIcon className="w-3.5 h-3.5 text-primary-400 group-hover:translate-x-0.5 transition-all" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* My Domains Tab (Unified Table) */}
                {activeTab === 'domains' && (
                    <div className="space-y-4 mb-8">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                            <div>
                                <h3 className="text-lg font-display font-bold text-white">My Domains</h3>
                                <p className="text-xs text-neutral-400">All your active, parked, offline, and moderation domains in one place</p>
                            </div>
                            <div className="flex items-center gap-3 w-full sm:w-auto">
                                <div className="relative flex-1 sm:max-w-xs w-full">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none select-none z-10">
                                        <SearchIcon className="w-3.5 h-3.5" />
                                    </span>
                                    <input
                                        type="text"
                                        value={domainSearch}
                                        onChange={(e) => setDomainSearch(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Escape' && domainSearch) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setDomainSearch('');
                                            }
                                        }}
                                        placeholder="Search my domains..."
                                        className="input !pl-9 pr-8 py-2 text-xs bg-dark-100/90 border border-dark-300 rounded-lg w-full"
                                    />
                                    {domainSearch && (
                                        <button
                                            type="button"
                                            onClick={() => setDomainSearch('')}
                                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white cursor-pointer p-0.5"
                                            title="Clear search"
                                        >
                                            <CrossIcon className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                                <button 
                                    onClick={() => setShowContactForm(true)}
                                    className="btn btn-primary px-4 py-2 text-sm whitespace-nowrap"
                                >
                                    <span className="relative z-10">Add Domain</span>
                                </button>
                            </div>
                        </div>

                        {/* Top Pagination for seamless switching without scrolling */}
                        <TablePagination
                            currentPage={domainsPage}
                            totalItems={filteredUserDomains.length}
                            pageSize={100}
                            onPageChange={setDomainsPage}
                            position="top"
                        />

                        {/* Domains Table with Unified Statuses */}
                        <div className="domainer-table-container my-2">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-white/10 bg-white/[0.02]">
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Domain</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Pricing</th>
                                            <th className="text-center py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Status</th>
                                            <th className="text-center py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                        {paginatedUserDomains.map((domain) => (
                                            <tr 
                                                key={domain.id} 
                                                onClick={() => handleEditDomain(domain)}
                                                className="border-b border-white/5 hover:bg-white/[0.04] transition-colors cursor-pointer group"
                                                title={`Click to edit ${domain.name}`}
                                            >
                                            <td className="py-3 px-4">
                                                <div>
                                                    <p className="text-white font-medium flex items-center gap-2">
                                                        <span>{domain.name}</span>
                                                        {domain.is_sold > 0 && (
                                                             <span className="px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/20 text-emerald-300">Sold</span>
                                                         )}
                                                         {domain.pending_description && (
                                                             <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-500/20 text-amber-300 border border-amber-500/30 font-semibold" title="Description edit is pending moderation">
                                                                 ⏳ Desc on review
                                                             </span>
                                                         )}
                                                     </p>
                                                     <p className="text-neutral-400 text-xs mt-0.5 max-w-xs truncate" title={domain.pending_description ? `Pending review: ${domain.pending_description}` : domain.description}>
                                                         {domain.pending_description ? (
                                                             <span className="text-amber-300/90 italic font-mono text-[11px]">{domain.pending_description}</span>
                                                         ) : (
                                                             domain.description
                                                         )}
                                                     </p>
                                                 </div>
                                             </td>
                                             <td className="py-3 px-4 text-xs space-y-1">
                                                 {domain.pricebuy && Number(domain.pricebuy) > 0 && (
                                                     <span className="inline-block px-2 py-0.5 rounded bg-primary-500/20 text-primary-300 mr-1 mb-1 font-medium whitespace-nowrap">
                                                         Buy: ${domain.discount_buy > 0 
                                                             ? (domain.pricebuy * (1 - domain.discount_buy / 100)).toLocaleString(undefined, {maximumFractionDigits: 0}) 
                                                             : Number(domain.pricebuy).toLocaleString()} {domain.discount_buy > 0 ? `(-${domain.discount_buy}%)` : ''}
                                                     </span>
                                                 )}
                                                 {domain.pricerent && Number(domain.pricerent) > 0 && (
                                                     <span className="inline-block px-2 py-0.5 rounded bg-secondary-500/20 text-secondary-300 mr-1 mb-1 font-medium whitespace-nowrap">
                                                         Rent: ${domain.discount_rent > 0 
                                                             ? (domain.pricerent * (1 - domain.discount_rent / 100)).toLocaleString(undefined, {maximumFractionDigits: 0}) 
                                                             : Number(domain.pricerent).toLocaleString()}/mo {domain.discount_rent > 0 ? `(-${domain.discount_rent}%)` : ''}
                                                     </span>
                                                 )}
                                                 {domain.pricefw && Number(domain.pricefw) > 0 && (
                                                     <span className="inline-block px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 mr-1 mb-1 font-medium whitespace-nowrap">
                                                         FW: ${domain.discount_fw > 0 
                                                             ? (domain.pricefw * (1 - domain.discount_fw / 100)).toLocaleString(undefined, {maximumFractionDigits: 0}) 
                                                             : Number(domain.pricefw).toLocaleString()}/day {domain.discount_fw > 0 ? `(-${domain.discount_fw}%)` : ''}
                                                     </span>
                                                 )}
                                                 {(!domain.pricebuy || Number(domain.pricebuy) <= 0) && (!domain.pricerent || Number(domain.pricerent) <= 0) && (!domain.pricefw || Number(domain.pricefw) <= 0) && (
                                                     <span className="text-neutral-500">—</span>
                                                 )}
                                             </td>
                                             <td className="py-3 px-4 text-center">
                                                 <span className={`px-2.5 py-1 rounded-full text-xs font-medium inline-block ${getStatusColor(domain.status)}`}>
                                                     {getStatusLabel(domain.status)}
                                                 </span>
                                             </td>
                                             <td className="py-3 px-4 text-center" onClick={(e) => e.stopPropagation()}>
                                                 <button 
                                                     type="button"
                                                     onClick={() => handleEditDomain(domain)}
                                                     className="text-primary-400 hover:text-primary-300 text-sm mr-3 font-medium cursor-pointer"
                                                 >
                                                     Edit
                                                 </button>
                                                 <button 
                                                     type="button"
                                                     onClick={() => handleDeleteDomain(domain.id, domain.name)}
                                                     className="text-red-400 hover:text-red-300 text-sm font-medium cursor-pointer"
                                                 >
                                                     Delete
                                                 </button>
                                             </td>
                                         </tr>
                                     ))}
                                     {filteredUserDomains.length === 0 && userDomains.length > 0 && (
                                         <tr>
                                             <td colSpan="4" className="py-8 text-center text-neutral-400 text-sm">
                                                 No domains match "{domainSearch}".{' '}
                                                 <button 
                                                     type="button" 
                                                     onClick={() => setDomainSearch('')} 
                                                     className="text-primary-400 hover:underline cursor-pointer font-medium"
                                                 >
                                                     Clear filter
                                                 </button>
                                             </td>
                                         </tr>
                                     )}
                                     {userDomains.length === 0 && (
                                         <tr>
                                             <td colSpan="4" className="py-12 text-center text-neutral-400 text-sm">
                                                You don't have any domains listed yet.{' '}
                                                <button
                                                    onClick={() => {
                                                        if (accessDisabled) return showDisabledUserError();
                                                        setShowContactForm(true);
                                                    }}
                                                    className="text-primary-400 hover:text-primary-300 underline font-medium"
                                                >
                                                    List your first domain
                                                </button>
                                                {' '}or use "Add Domain for Sale" in the navigation bar.
                                            </td>
                                        </tr>
                                     )}
                                </tbody>
                            </table>
                        </div>
                        </div>
                        <TablePagination
                            currentPage={domainsPage}
                            totalItems={filteredUserDomains.length}
                            pageSize={100}
                            onPageChange={setDomainsPage}
                        />
                    </div>
                )}

                {/* Activity Log Tab */}
                {activeTab === 'activity' && (
                    <div className="space-y-4 mb-8">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                            <h3 className="text-lg font-display font-bold text-white">Activity Log</h3>
                            {userActivity.length > 5 && (
                                <div className="relative flex-1 sm:max-w-xs w-full">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none select-none z-10">
                                        <SearchIcon className="w-3.5 h-3.5" />
                                    </span>
                                    <input
                                        type="text"
                                        value={activitySearch}
                                        onChange={(e) => setActivitySearch(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Escape' && activitySearch) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setActivitySearch('');
                                            }
                                        }}
                                        placeholder="Search activity..."
                                        className="input !pl-9 pr-8 py-2 text-xs bg-dark-100/90 border border-dark-300 rounded-lg w-full"
                                    />
                                    {activitySearch && (
                                        <button
                                            type="button"
                                            onClick={() => setActivitySearch('')}
                                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white cursor-pointer p-0.5"
                                            title="Clear search"
                                        >
                                            <CrossIcon className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Top Pagination for seamless switching without scrolling */}
                        <TablePagination
                            currentPage={activityPage}
                            totalItems={filteredUserActivity.length}
                            pageSize={100}
                            onPageChange={setActivityPage}
                            position="top"
                        />

                        <div className="domainer-table-container my-2">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-white/10 bg-white/[0.02]">
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Action</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Details</th>
                                            <th className="text-left py-3.5 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">Timestamp</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/5">
                                        {paginatedUserActivity.map((activity, index) => (
                                            <tr key={index} className="border-b border-white/5 hover:bg-white/[0.035] transition-colors">
                                            <td className="py-3 px-4">
                                                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
                                                    activity.action === 'domain_approved' || activity.action === 'description_approved' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' :
                                                    activity.action === 'domain_rejected' || activity.action === 'description_rejected' ? 'bg-red-500/20 text-red-300 border border-red-500/30' :
                                                    activity.action === 'submit_domain' || activity.action === 'submit_description_moderation' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' :
                                                    activity.action === 'delete_domain' ? 'bg-neutral-500/20 text-neutral-300 border border-neutral-500/30' :
                                                    'bg-primary-500/20 text-primary-300 border border-primary-500/30'
                                                }`}>
                                                    {(activity.action || '').replace(/_/g, ' ')}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4">
                                                <p className="text-neutral-400 text-sm">{activity.details}</p>
                                            </td>
                                            <td className="py-3 px-4">
                                                <p className="text-neutral-400 text-sm">{formatTimestamp(activity.timestamp)}</p>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredUserActivity.length === 0 && userActivity.length > 0 && (
                                        <tr>
                                            <td colSpan="3" className="py-8 text-center text-neutral-400 text-sm">
                                                No activity matches "{activitySearch}".{' '}
                                                <button
                                                    type="button"
                                                    onClick={() => setActivitySearch('')}
                                                    className="text-primary-400 hover:underline cursor-pointer font-medium"
                                                >
                                                    Clear filter
                                                </button>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                            {userActivity.length === 0 && (
                                <div className="text-center py-8">
                                     <p className="text-neutral-400">No activity found</p>
                                </div>
                            )}
                        </div>
                        </div>
                        <TablePagination
                            currentPage={activityPage}
                            totalItems={filteredUserActivity.length}
                            pageSize={100}
                            onPageChange={setActivityPage}
                        />
                    </div>
                )}

                {/* Edit Domain Modal with FormTabs and FormSecurityNotice */}
                {showEditModal && (
                    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
                         onClick={(e) => {
                             if (e.target === e.currentTarget) setShowEditModal(false);
                         }}>
                        <div className="w-full max-w-lg my-8 relative">
                            {/* Offset Floating Close Button */}
                            <button 
                                type="button"
                                onClick={() => setShowEditModal(false)}
                                className="absolute -top-3 -right-3 sm:-top-3.5 sm:-right-3.5 w-9 h-9 rounded-full bg-neutral-900/95 hover:bg-neutral-800 text-neutral-400 hover:text-white border border-white/[0.18] hover:border-white/[0.35] flex items-center justify-center transition-all duration-200 active:scale-90 shadow-2xl z-50 cursor-pointer group shrink-0"
                                title="Close"
                                aria-label="Close modal"
                            >
                                <CrossIcon className="w-3.5 h-3.5 transition-transform duration-200 group-hover:rotate-90 group-hover:scale-110" />
                            </button>

                            {/* Bookmark tab sticking out of the modal window */}
                            <div className="relative z-20">
                                <FormTabs activeTab="edit" />
                            </div>

                            <div className="bg-neutral-950/95 backdrop-blur-3xl rounded-3xl border border-white/[0.16] ring-1 ring-white/[0.08] max-h-[85vh] overflow-y-auto shadow-2xl relative z-10">
                                <div className="p-6 pt-5">
                                    <div className="flex items-center justify-between pb-4 border-b border-white/10 mb-4">
                                        <div>
                                            <h3 className="text-lg font-bold text-white">Edit Domain Pricing</h3>
                                            <p className="text-xs text-neutral-400">Update monetization settings and discounts (max 50%)</p>
                                        </div>
                                    </div>

                                    {editFormErrors.general && (
                                        <div className="mb-4 p-3 bg-red-500/20 text-red-300 rounded-lg text-xs border border-red-500/30">
                                            {editFormErrors.general}
                                        </div>
                                    )}

                                    <div className="space-y-4">
                                        {/* Floating Label: Domain (Read-Only) */}
                                        <div className="relative pt-1">
                                            <input 
                                                type="text" 
                                                id="edit_domain_name"
                                                value={editForm.name}
                                                readOnly
                                                placeholder=" "
                                                className="peer block w-full px-3.5 py-2.5 text-sm text-neutral-400 bg-neutral-900/50 rounded-xl border border-white/[0.10] cursor-not-allowed placeholder-transparent"
                                            />
                                            <label
                                                htmlFor="edit_domain_name"
                                                className="absolute start-3 -top-1.5 z-10 px-1.5 text-xs text-neutral-500 bg-neutral-950 rounded pointer-events-none font-medium"
                                            >
                                                Domain Name (Locked)
                                            </label>
                                        </div>

                                        {/* Moderation status banner placed outside relative container to avoid overlapping floating label */}
                                        {editingDomain?.pending_description && (
                                            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-medium">
                                                <ClockIcon className="w-3.5 h-3.5 shrink-0" />
                                                <span>Description is currently under admin moderation</span>
                                            </div>
                                        )}

                                        {/* Floating Label: Description */}
                                        <div className="relative pt-1">
                                            <textarea 
                                                id="edit_description"
                                                value={editForm.description}
                                                disabled={isAiFillingEdit}
                                                onChange={(e) => {
                                                    setEditForm({...editForm, description: e.target.value});
                                                    if (editFormErrors.description) setEditFormErrors({...editFormErrors, description: ''});
                                                }}
                                                rows={4}
                                                placeholder=" "
                                                className={`peer block w-full px-3.5 pr-14 py-2.5 min-h-[90px] text-sm text-white bg-neutral-900/90 rounded-xl border border-white/[0.12] appearance-none focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-all placeholder-transparent resize-y ${
                                                    isAiFillingEdit ? 'opacity-60 cursor-not-allowed bg-neutral-900/50' : ''
                                                }`}
                                            />
                                            <label
                                                htmlFor="edit_description"
                                                className="absolute start-3 -top-1.5 z-10 px-1.5 text-xs text-neutral-400 duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-3.5 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:text-sm peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-1.5 peer-focus:translate-y-0 peer-focus:text-xs peer-focus:text-primary-400 peer-focus:bg-neutral-950 font-medium"
                                            >
                                                Short Description
                                            </label>

                                            {/* AI Sparkles Auto-fill button with yellow label */}
                                            <button
                                                type="button"
                                                disabled={isAiFillingEdit}
                                                onClick={handleAutoFillEditDomain}
                                                className="absolute top-2 right-2 z-10 h-7 px-2 rounded-lg flex items-center gap-1.5 bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/35 transition-all hover:scale-[1.03] active:scale-95 disabled:opacity-50 cursor-pointer shadow-sm group"
                                                title="Auto-fill description and suggested pricing"
                                                aria-label="Auto-fill description and suggested pricing"
                                            >
                                                {isAiFillingEdit ? (
                                                    <>
                                                        <div className="w-3.5 h-3.5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                                                        <span className="text-[11px] font-bold text-amber-300 tracking-wide">Auto</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <WandIcon className="w-3.5 h-3.5 text-amber-400 group-hover:rotate-12 transition-transform duration-200" />
                                                        <span className="text-[11px] font-bold text-amber-300 tracking-wide">Auto</span>
                                                    </>
                                                )}
                                            </button>
                                            {editFormErrors.description ? (
                                                <p className="mt-1 text-xs text-red-400">{editFormErrors.description}</p>
                                            ) : editingDomain?.pending_description ? (
                                                <p className="mt-1 text-[11px] text-neutral-400">
                                                    Current public: <span className="text-neutral-300 italic">"{editingDomain?.description || 'None'}"</span>. Edits saved here will update your pending review.
                                                </p>
                                            ) : editingDomain?.status !== 2 ? (
                                                <p className="mt-1 text-[11px] text-neutral-400 flex items-center gap-1.5">
                                                    <InfoIcon className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                                                    <span>Description changes will take effect after admin moderation to prevent fraud.</span>
                                                </p>
                                            ) : null}
                                        </div>

                                        <div className="space-y-3 pt-1">
                                            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider">Monetization Options</p>
                                            
                                            {/* Option 1: Buy */}
                                            <div className={`p-4 rounded-xl border transition-all ${editForm.enable_buy ? 'bg-primary-500/5 border-primary-500/30' : 'bg-dark-200/30 border-dark-300/40 opacity-75'}`}>
                                                <label className="flex items-center gap-3 cursor-pointer select-none">
                                                    <input 
                                                        type="checkbox"
                                                        checked={editForm.enable_buy}
                                                        onChange={(e) => setEditForm({...editForm, enable_buy: e.target.checked})}
                                                        className="w-4 h-4 rounded text-primary-500 bg-dark-300 border-dark-300 focus:ring-primary-500"
                                                    />
                                                    <div className="flex items-center gap-2">
                                                        <BuyIcon className="w-4 h-4 text-primary-400" />
                                                        <span className="text-white font-medium text-sm">Buy (Permanent Sale)</span>
                                                    </div>
                                                    <span className="text-xs text-neutral-400 ml-auto">Min: ${minPrices.min_price_buy || 100}</span>
                                                </label>
                                                {editForm.enable_buy && (
                                                    <div className="grid grid-cols-2 gap-3 pt-3 mt-3 border-t border-dark-300/40">
                                                        <div className="relative">
                                                            <input 
                                                                type="number"
                                                                id="edit_pricebuy"
                                                                value={editForm.pricebuy}
                                                                onChange={(e) => setEditForm({...editForm, pricebuy: e.target.value})}
                                                                placeholder=" "
                                                                min={minPrices.min_price_buy || 100}
                                                                className="peer block w-full px-3 py-2 text-xs text-white bg-neutral-950/80 rounded-xl border border-white/[0.12] focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 focus:outline-none transition-all placeholder-transparent"
                                                            />
                                                            <label htmlFor="edit_pricebuy" className="absolute start-2.5 -top-2 z-10 px-1 text-[10px] text-neutral-400 duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-xs peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:text-primary-400 peer-focus:bg-neutral-950 font-medium">
                                                                Buy Price ($)
                                                            </label>
                                                            {editFormErrors.pricebuy && <p className="text-[11px] text-red-400 mt-1">{editFormErrors.pricebuy}</p>}
                                                        </div>
                                                        <div className="relative">
                                                            <input 
                                                                type="number"
                                                                id="edit_discount_buy"
                                                                value={editForm.discount_buy}
                                                                onChange={(e) => setEditForm({...editForm, discount_buy: e.target.value})}
                                                                placeholder=" "
                                                                min="0"
                                                                max="50"
                                                                className="peer block w-full px-3 py-2 text-xs text-white bg-neutral-950/80 rounded-xl border border-white/[0.12] focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 focus:outline-none transition-all placeholder-transparent"
                                                            />
                                                            <label htmlFor="edit_discount_buy" className="absolute start-2.5 -top-2 z-10 px-1 text-[10px] text-neutral-400 duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-xs peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:text-primary-400 peer-focus:bg-neutral-950 font-medium">
                                                                Discount % (max 50%)
                                                            </label>
                                                            {editFormErrors.discount_buy && <p className="text-[11px] text-red-400 mt-1">{editFormErrors.discount_buy}</p>}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Option 2: Rent */}
                                            <div className={`p-4 rounded-xl border transition-all ${editForm.enable_rent ? 'bg-secondary-500/5 border-secondary-500/30' : 'bg-dark-200/30 border-dark-300/40 opacity-75'}`}>
                                                <label className="flex items-center gap-3 cursor-pointer select-none">
                                                    <input 
                                                        type="checkbox"
                                                        checked={editForm.enable_rent}
                                                        onChange={(e) => setEditForm({...editForm, enable_rent: e.target.checked})}
                                                        className="w-4 h-4 rounded text-secondary-500 bg-dark-300 border-dark-300 focus:ring-secondary-500"
                                                    />
                                                    <div className="flex items-center gap-2">
                                                        <RentIcon className="w-4 h-4 text-secondary-400" />
                                                        <span className="text-white font-medium text-sm">Rent (Monthly Lease)</span>
                                                    </div>
                                                    <span className="text-xs text-neutral-400 ml-auto">Min: ${minPrices.min_price_rent || 50}/mo</span>
                                                </label>
                                                {editForm.enable_rent && (
                                                    <div className="grid grid-cols-2 gap-3 pt-3 mt-3 border-t border-dark-300/40">
                                                        <div className="relative">
                                                            <input 
                                                                type="number"
                                                                id="edit_pricerent"
                                                                value={editForm.pricerent}
                                                                onChange={(e) => setEditForm({...editForm, pricerent: e.target.value})}
                                                                placeholder=" "
                                                                min={minPrices.min_price_rent || 50}
                                                                className="peer block w-full px-3 py-2 text-xs text-white bg-neutral-950/80 rounded-xl border border-white/[0.12] focus:border-secondary-500 focus:ring-1 focus:ring-secondary-500/30 focus:outline-none transition-all placeholder-transparent"
                                                            />
                                                            <label htmlFor="edit_pricerent" className="absolute start-2.5 -top-2 z-10 px-1 text-[10px] text-neutral-400 duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-xs peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:text-secondary-400 peer-focus:bg-neutral-950 font-medium">
                                                                Rent Price ($/mo)
                                                            </label>
                                                            {editFormErrors.pricerent && <p className="text-[11px] text-red-400 mt-1">{editFormErrors.pricerent}</p>}
                                                        </div>
                                                        <div className="relative">
                                                            <input 
                                                                type="number"
                                                                id="edit_discount_rent"
                                                                value={editForm.discount_rent}
                                                                onChange={(e) => setEditForm({...editForm, discount_rent: e.target.value})}
                                                                placeholder=" "
                                                                min="0"
                                                                max="50"
                                                                className="peer block w-full px-3 py-2 text-xs text-white bg-neutral-950/80 rounded-xl border border-white/[0.12] focus:border-secondary-500 focus:ring-1 focus:ring-secondary-500/30 focus:outline-none transition-all placeholder-transparent"
                                                            />
                                                            <label htmlFor="edit_discount_rent" className="absolute start-2.5 -top-2 z-10 px-1 text-[10px] text-neutral-400 duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-xs peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:text-secondary-400 peer-focus:bg-neutral-950 font-medium">
                                                                Discount % (max 50%)
                                                            </label>
                                                            {editFormErrors.discount_rent && <p className="text-[11px] text-red-400 mt-1">{editFormErrors.discount_rent}</p>}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Option 3: Forwarding */}
                                            <div className={`p-4 rounded-xl border transition-all ${editForm.enable_forward ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-dark-200/30 border-dark-300/40 opacity-75'}`}>
                                                <label className="flex items-center gap-3 cursor-pointer select-none">
                                                    <input 
                                                        type="checkbox"
                                                        checked={editForm.enable_forward}
                                                        onChange={(e) => setEditForm({...editForm, enable_forward: e.target.checked})}
                                                        className="w-4 h-4 rounded text-emerald-500 bg-dark-300 border-dark-300 focus:ring-emerald-500"
                                                    />
                                                    <div className="flex items-center gap-2">
                                                        <ForwardIcon className="w-4 h-4 text-emerald-400" />
                                                        <span className="text-white font-medium text-sm">Forwarding (Daily Traffic)</span>
                                                    </div>
                                                    <span className="text-xs text-neutral-400 ml-auto">Min: ${minPrices.min_price_forward || 10}/day</span>
                                                </label>
                                                {editForm.enable_forward && (
                                                    <div className="grid grid-cols-2 gap-3 pt-3 mt-3 border-t border-dark-300/40">
                                                        <div className="relative">
                                                            <input 
                                                                type="number"
                                                                id="edit_pricefw"
                                                                value={editForm.pricefw}
                                                                onChange={(e) => setEditForm({...editForm, pricefw: e.target.value})}
                                                                placeholder=" "
                                                                min={minPrices.min_price_forward || 10}
                                                                className="peer block w-full px-3 py-2 text-xs text-white bg-neutral-950/80 rounded-xl border border-white/[0.12] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 focus:outline-none transition-all placeholder-transparent"
                                                            />
                                                            <label htmlFor="edit_pricefw" className="absolute start-2.5 -top-2 z-10 px-1 text-[10px] text-neutral-400 duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-xs peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:text-emerald-400 peer-focus:bg-neutral-950 font-medium">
                                                                Forward Price ($/day)
                                                            </label>
                                                            {editFormErrors.pricefw && <p className="text-[11px] text-red-400 mt-1">{editFormErrors.pricefw}</p>}
                                                        </div>
                                                        <div className="relative">
                                                            <input 
                                                                type="number"
                                                                id="edit_discount_fw"
                                                                value={editForm.discount_fw}
                                                                onChange={(e) => setEditForm({...editForm, discount_fw: e.target.value})}
                                                                placeholder=" "
                                                                min="0"
                                                                max="50"
                                                                className="peer block w-full px-3 py-2 text-xs text-white bg-neutral-950/80 rounded-xl border border-white/[0.12] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 focus:outline-none transition-all placeholder-transparent"
                                                            />
                                                            <label htmlFor="edit_discount_fw" className="absolute start-2.5 -top-2 z-10 px-1 text-[10px] text-neutral-400 duration-200 transform origin-left bg-neutral-950 rounded pointer-events-none peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:text-xs peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-neutral-500 peer-focus:-top-2 peer-focus:translate-y-0 peer-focus:text-[10px] peer-focus:text-emerald-400 peer-focus:bg-neutral-950 font-medium">
                                                                Discount % (max 50%)
                                                            </label>
                                                            {editFormErrors.discount_fw && <p className="text-[11px] text-red-400 mt-1">{editFormErrors.discount_fw}</p>}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-end space-x-3 mt-6 pt-2">
                                        <button 
                                            type="button"
                                            onClick={() => setShowEditModal(false)}
                                            className="btn btn-ghost px-4 py-2 text-sm"
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={handleUpdateDomain}
                                            className="btn btn-primary px-5 py-2 text-sm"
                                        >
                                            <span className="relative z-10">Save Changes</span>
                                        </button>
                                    </div>
                                </div>

                                <FormSecurityNotice />
                            </div>
                        </div>

                        {/* Auto-fill Confirmation Card for Edit Domain (100% solid, non-transparent background) */}
                        {showEditAiConfirm && (
                            <div className="absolute inset-x-4 top-20 z-50 flex justify-center animate-fadeIn pointer-events-auto">
                                <div 
                                    style={{ backgroundColor: '#101014', opacity: 1 }}
                                    className="bg-neutral-950 border border-amber-500/40 rounded-2xl p-4 max-w-[340px] w-full shadow-[0_25px_70px_rgba(0,0,0,1),0_0_0_1px_rgba(255,255,255,0.08)] space-y-3 animate-modal-strict relative overflow-hidden"
                                >
                                    {/* 100% solid opaque background layer to prevent any transparency */}
                                    <div className="absolute inset-0 bg-[#101014] pointer-events-none -z-10" style={{ backgroundColor: '#101014' }} />
                                    <div className="flex items-start gap-2.5">
                                        <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 shrink-0 mt-0.5 shadow-sm">
                                            <WandIcon className="w-4 h-4" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <h4 className="text-xs sm:text-sm font-semibold text-white leading-tight">
                                                Replace description & prices?
                                            </h4>
                                            <p className="text-[11px] text-neutral-400 mt-1 leading-snug">
                                                Current text and prices will be replaced with suggested listing details.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/[0.08]">
                                        <button
                                            type="button"
                                            onClick={() => setShowEditAiConfirm(false)}
                                            className="px-2.5 py-1 rounded-lg text-xs font-medium text-neutral-400 hover:text-white hover:bg-white/[0.06] transition-colors cursor-pointer"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={executeAutoFillEdit}
                                            className="px-3 py-1 rounded-lg text-xs font-semibold bg-amber-500 hover:bg-amber-400 active:scale-95 text-neutral-950 shadow-md shadow-amber-500/20 transition-all cursor-pointer flex items-center gap-1.5"
                                        >
                                            <WandIcon className="w-3.5 h-3.5" />
                                            <span>Auto-fill</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Unified Add/Sell Domain Modal */}
                {showContactForm && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
                         onClick={(e) => {
                             if (e.target === e.currentTarget) setShowContactForm(false);
                         }}>
                        <div className="relative">
                            <ContactForm 
                                onClose={() => setShowContactForm(false)} 
                                isPurchase={false}
                                onSubmitted={(data) => {
                                    setShowContactForm(false);
                                    showNotice(`Domain ${data?.domainName || ''} submitted for moderation successfully! Our team will review it.`);
                                    fetchUserDomains();
                                    fetchUserActivity();
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Error Modal for Disabled Users */}
                {showErrorModal && (
                    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="card p-6 max-w-md w-full">
                            <h3 className="text-lg font-display font-bold text-white mb-4">Access Denied</h3>
                            <p className="text-neutral-400 mb-6">
                                Your account is disabled. Please contact support for more information.
                            </p>
                            <div className="flex justify-end">
                                <button 
                                    onClick={() => setShowErrorModal(false)}
                                    className="btn btn-primary px-4 py-2 text-sm"
                                >
                                    <span className="relative z-10">OK</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PersonalCabinet;